import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Mail,
  MessageSquare,
  User,
  Send,
  Sparkles,
  Copy,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'general',      label: 'General question' },
  { key: 'order_issue',  label: 'Order issue' },
  { key: 'rewards',      label: 'Rewards / gift credits' },
  { key: 'restaurant',   label: 'I run a restaurant' },
  { key: 'billing',      label: 'Billing / subscription' },
  { key: 'other',        label: 'Something else' },
];

const inputBase =
  'w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-white/40 text-[14px] focus:outline-none focus:border-yellow-500/55 focus:bg-white/[0.05] transition-colors';

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null); // { reference_id }

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.email.includes('@')) {
      toast.error('Enter a valid email so we can reply.');
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error('Please describe your issue (5+ characters).');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/support/submit`, {
        name: form.name.trim() || null,
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim() || null,
        category: form.category,
        message: form.message.trim(),
      });
      setSent({ reference_id: r.data?.reference_id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(sent.reference_id);
      toast.success('Reference ID copied');
    } catch { /* ignore */ }
  };

  return (
    <LanguageProvider>
      <div className="min-h-screen bg-black text-white">
        <SEO
          title="Contact NoFee Support — We reply within 24 hours"
          description="Have a question about NoFee? Send our support team a message and we'll get back to you within 24 business hours."
          path="/contact"
        />
        <Navbar />

        <main className="pt-28 sm:pt-32 pb-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
                <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                  We're here to help
                </span>
              </div>
              <h1
                data-testid="contact-page-headline"
                className="mt-5 font-['Outfit',_Inter,_sans-serif] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.05]"
              >
                Contact{' '}
                <span className="bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent">
                  NoFee Support
                </span>
              </h1>
              <p className="mt-4 text-[14.5px] sm:text-[15px] text-white/60 max-w-xl mx-auto leading-relaxed">
                Send us a message and we'll reply within{' '}
                <span className="text-white font-semibold">24 business hours</span> — usually much sooner.
                You'll receive an instant email confirmation with a unique reference ID.
              </p>
            </div>

            {sent ? (
              // Success view
              <div
                data-testid="contact-success-view"
                className="p-7 sm:p-10 rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] via-zinc-950 to-black text-center"
              >
                <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/40 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300" strokeWidth={1.7} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Your message has been received
                </h2>
                <p className="mt-3 text-[14px] text-white/65 max-w-md mx-auto">
                  A confirmation email is on the way to{' '}
                  <strong className="text-white">{form.email}</strong>. Please save your reference
                  ID in case you need to follow up.
                </p>

                <div className="mt-7 inline-flex flex-col items-center gap-2 px-7 py-5 rounded-2xl border border-yellow-500/45 bg-yellow-500/[0.05]">
                  <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                    Support Reference ID
                  </span>
                  <div className="flex items-center gap-2">
                    <code
                      data-testid="contact-reference-id"
                      className="font-mono text-2xl sm:text-3xl font-bold text-white tracking-wider"
                    >
                      {sent.reference_id}
                    </code>
                    <button
                      type="button"
                      onClick={copyRef}
                      data-testid="contact-copy-ref"
                      aria-label="Copy reference ID"
                      className="ml-1 w-9 h-9 rounded-lg border border-white/12 hover:border-yellow-500/45 text-white/65 hover:text-yellow-200 inline-flex items-center justify-center transition-all"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <p className="mt-6 text-[12.5px] text-white/45">
                  Didn't see the email? Check your Spam/Junk folder. You can also reach us directly at{' '}
                  <a href="mailto:support@nofee.restaurant" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    support@nofee.restaurant
                  </a>.
                </p>

                <div className="mt-7 flex flex-col sm:flex-row gap-2.5 justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setSent(null);
                      setForm({ name: '', email: '', subject: '', category: 'general', message: '' });
                    }}
                    data-testid="contact-send-another"
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl border border-white/12 hover:border-yellow-500/40 text-white/85 hover:text-white text-[13px] font-bold transition-all"
                  >
                    Send another message
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black text-[13px] font-extrabold"
                  >
                    Back to home <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ) : (
              // Form view
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="p-6 sm:p-8 rounded-3xl border border-white/8 bg-white/[0.015] space-y-4"
              >
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11.5px] text-white/55 font-medium">Your name</span>
                    <div className="mt-1 relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        value={form.name}
                        onChange={onChange('name')}
                        placeholder="Jane Doe"
                        className={`${inputBase} pl-10`}
                        autoComplete="name"
                      />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-[11.5px] text-white/55 font-medium">Email <span className="text-rose-400">*</span></span>
                    <div className="mt-1 relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange('email')}
                        placeholder="you@email.com"
                        className={`${inputBase} pl-10`}
                        autoComplete="email"
                      />
                    </div>
                  </label>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11.5px] text-white/55 font-medium">Category</span>
                    <select
                      data-testid="contact-category-input"
                      value={form.category}
                      onChange={onChange('category')}
                      className={`${inputBase} mt-1 appearance-none bg-no-repeat bg-[right_14px_center]`}
                      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\'%3E%3Cpath d=\'M1 1l4 4 4-4\' stroke=\'%23999\' stroke-width=\'1.5\'/%3E%3C/svg%3E")' }}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key} className="bg-zinc-900 text-white">
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[11.5px] text-white/55 font-medium">Subject</span>
                    <input
                      data-testid="contact-subject-input"
                      type="text"
                      value={form.subject}
                      onChange={onChange('subject')}
                      placeholder="Short summary"
                      maxLength={140}
                      className={`${inputBase} mt-1`}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-[11.5px] text-white/55 font-medium">
                    Message <span className="text-rose-400">*</span>
                  </span>
                  <div className="mt-1 relative">
                    <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-white/35" />
                    <textarea
                      data-testid="contact-message-input"
                      required
                      value={form.message}
                      onChange={onChange('message')}
                      rows={6}
                      maxLength={4000}
                      placeholder="Tell us what's going on. The more detail, the faster we can help."
                      className={`${inputBase} pl-10 pt-3.5 resize-y min-h-[150px]`}
                    />
                  </div>
                  <span className="mt-1.5 block text-right text-[11px] text-white/35 tabular-nums">
                    {form.message.length} / 4000
                  </span>
                </label>

                <p className="text-[12px] text-white/40 leading-relaxed">
                  By submitting, you agree to our{' '}
                  <Link to="/privacy" className="text-yellow-300 hover:text-yellow-200">Privacy Policy</Link>.
                  We only use your email to reply to this request.
                </p>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Sending…' : 'Send message'}
                </button>

                <p className="text-[12px] text-white/40 text-center leading-relaxed">
                  Prefer email? Write to{' '}
                  <a href="mailto:support@nofee.restaurant" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    support@nofee.restaurant
                  </a>
                </p>
              </form>
            )}
          </div>
        </main>

        <FooterSection />
      </div>
    </LanguageProvider>
  );
};

export default ContactPage;
